import type { AsyncBelongsTo, AsyncHasMany } from '@warp-drive/legacy/model';

/**
 * Extracts the assignable value type for an `AsyncBelongsTo` relationship.
 *
 * Given an `AsyncBelongsTo<Related>` field, this resolves to `Related | null`,
 * which is what Ember Data / WarpDrive expects when you assign to the
 * relationship at runtime.
 *
 * @typeParam T - The type of the relationship field (usually `AsyncBelongsTo<Related>`).
 */
type AsyncBelongsToValue<T> =
  T extends AsyncBelongsTo<infer Related> ? Related | null : never;

/**
 * Extracts the assignable value type for an `AsyncHasMany` relationship.
 *
 * Given an `AsyncHasMany<Related>` field, this resolves to `Related[]`,
 * which is what Ember Data / WarpDrive expects when you assign to the
 * relationship at runtime.
 *
 * @typeParam T - The type of the relationship field (usually `AsyncHasMany<Related>`).
 */
type AsyncHasManyValue<T> =
  T extends AsyncHasMany<infer Related> ? Related[] : never;

/**
 * Sets an async `belongsTo` relationship on a record in a type-safe way.
 *
 * This helper keeps the field itself correctly typed as `AsyncBelongsTo<Related>`
 * (for accurate reads like `await record.relationship`) while still allowing
 * you to assign the underlying `Related | null` value that Ember Data expects
 * at runtime.
 *
 * All unsafe casting is centralized inside this function so that call sites
 * remain fully type-checked and free of `as any`.
 *
 * @typeParam R - The record type (e.g. `User`).
 * @typeParam K - The key on the record that refers to an `AsyncBelongsTo` field.
 * @param record - The record instance to mutate.
 * @param key - The relationship key to set on the record.
 * @param value - The related record (or `null`) to assign to the relationship.
 *
 * @example
 * ```ts
 * declare let user: User;
 * declare let bestFriend: User | null;
 *
 * setAsyncBelongsTo(user, 'bestFriend', bestFriend);
 * ```
 */
export function setAsyncBelongsTo<R, K extends keyof R>(
  record: R,
  key: K,
  value: AsyncBelongsToValue<R[K]>
) {
  // Single, centralized escape hatch:
  (record as Record<K, R[K]>)[key] = value as unknown as R[K];
}

/**
 * Sets an async `hasMany` relationship on a record in a type-safe way.
 *
 * This helper keeps the field itself correctly typed as `AsyncHasMany<Related>`
 * (for accurate reads like `await record.relationship`) while still allowing
 * you to assign the underlying `Related[]` value that Ember Data expects
 * at runtime.
 *
 * All unsafe casting is centralized inside this function so that call sites
 * remain fully type-checked and free of `as any`.
 *
 * @typeParam R - The record type (e.g. `User`).
 * @typeParam K - The key on the record that refers to an `AsyncHasMany` field.
 * @param record - The record instance to mutate.
 * @param key - The relationship key to set on the record.
 * @param value - The array of related records to assign to the relationship.
 *
 * @example
 * ```ts
 * declare let user: User;
 * declare let alice: User;
 * declare let bob: User;
 *
 * setAsyncHasMany(user, 'friends', [alice, bob]);
 * ```
 */
export function setAsyncHasMany<R, K extends keyof R>(
  record: R,
  key: K,
  value: AsyncHasManyValue<R[K]>
) {
  // Single, centralized assertion; call sites stay fully type-safe
  (record as Record<K, R[K]>)[key] = value as unknown as R[K];
}

/**
 * Casts an array of items to an async has many.
 *
 * @param items - The array of items to convert to an async has many.
 * @returns The array of items as an async has many.
 */
export function asAsyncHasMany<T>(items: T[]): AsyncHasManyValue<T[]> {
  return items as unknown as AsyncHasManyValue<T[]>;
}

/**
 * Casts an item to an async belongs to.
 *
 * @param item - The item to convert to an async belongs to.
 * @returns The item as an async belongs to.
 */
export function asAsyncBelongsTo<T>(item: T): AsyncBelongsToValue<T> {
  return item as unknown as AsyncBelongsToValue<T>;
}
